import { faRightFromBracket, faRightToBracket, faTableList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Container, Stack, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DataHandler } from "../dataHandler";
import LogoHeader from "../logoHeader/logoHeader";
import ModalAlert from "../sharedComponents/modalAlert";
import { HydroSpinner } from "../spinner/hydroSpinner";
import { Buchung, formatStunden, heute, summeMinuten, ZeitStatus } from "./zeitData";

type Meldung = {
    text: string,
    variant: 'success' | 'warning'
}

/**
 * Kommen und Gehen. Gerundet wird im Backend (Kommen auf die nächste,
 * Gehen auf die letzte volle Viertelstunde), hier wird nur gebucht und angezeigt.
 */
export default function Zeit() {

    const [status, setStatus] = useState<ZeitStatus | null | undefined>();
    const [heuteZeiten, setHeuteZeiten] = useState<Array<Buchung>>([]);
    const [meldung, setMeldung] = useState<Meldung | undefined>();
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();

    const navigate = useNavigate();

    /** Abgelaufene Session führt zurück zum Login, alles andere in den Fehlerdialog. */
    const zeigeFehler = useCallback((text: string) => {
        if (text === DataHandler.SESSION_EXPIRED)
            navigate('/login');
        else
            setError(text);
    }, [navigate]);

    /** Editoren bekommen alle Benutzer geliefert, angezeigt werden nur die eigenen Zeiten. */
    const ladeHeute = useCallback((user: string): Promise<Array<Buchung>> => {
        return DataHandler.getZeitList(heute(), heute()).then((zeiten) => {
            const eigene = zeiten.filter((z) => z.user === user);
            setHeuteZeiten(eigene);
            return eigene;
        });
    }, []);

    useEffect(() => {
        DataHandler.getZeitStatus().then((s) => {
            setStatus(s);
            ladeHeute(s.user).catch(zeigeFehler);
        }).catch((text: string) => {
            setStatus(null);
            zeigeFehler(text);
        });
    }, [ladeHeute, zeigeFehler]);

    const meldungFuer = (aktion: 'kommen' | 'gehen', s: ZeitStatus, zeiten: Array<Buchung>): Meldung => {
        if (!s.gebucht)
            return {
                variant: 'warning',
                text: aktion === 'kommen'
                    ? 'Sie sind bereits anwesend. Das zweite Kommen wurde ignoriert.'
                    : 'Es war keine Buchung offen. Das zweite Gehen wurde ignoriert.'
            };
        if (aktion === 'kommen')
            return { variant: 'success', text: 'Kommen gebucht: ' + (s.offeneBuchung ? s.offeneBuchung.kommenZeit : '') + ' Uhr.' };

        const beendet = zeiten.filter((z) => !z.offen);
        const letzte = beendet.length > 0 ? beendet[beendet.length - 1] : undefined;
        return { variant: 'success', text: 'Gehen gebucht' + (letzte ? ': ' + letzte.gehenZeit + ' Uhr.' : '.') };
    }

    const buchen = (aktion: 'kommen' | 'gehen') => {
        setBusy(true);
        setMeldung(undefined);
        const request = aktion === 'kommen' ? DataHandler.zeitKommen() : DataHandler.zeitGehen();
        request.then((s) => {
            setStatus(s);
            return ladeHeute(s.user).then((zeiten) => {
                setMeldung(meldungFuer(aktion, s, zeiten));
            });
        }).catch(zeigeFehler).finally(() => {
            setBusy(false);
        });
    }

    const zeitenliste = () => {
        navigate('/zeitliste');
    }

    const back = () => {
        navigate('/');
    }

    if (status === undefined)
        return (<HydroSpinner />);

    if (status === null)
        return (<Container>
            <LogoHeader />
            {error && <ModalAlert callback={() => setError(undefined)} content={error} contentTitle="Fehler" variant="danger"></ModalAlert>}
            <Container>
                <h3>Konnte Daten nicht laden.</h3>
                <Button onClick={back} variant="secondary">Zurück</Button>
            </Container>
        </Container>);

    const offen = status.offeneBuchung;
    const stackClass = 'justify-content-center col-md-5 mx-auto ';

    return (
        <Container>
            {error && <ModalAlert callback={() => setError(undefined)} content={error} contentTitle="Fehler" variant="danger"></ModalAlert>}
            <LogoHeader />
            <Stack className={stackClass} gap={3}>
                <h4 className="text-center">Zeiterfassung {status.name}</h4>

                <Alert variant={status.anwesend ? 'success' : 'secondary'} className="text-center mb-0">
                    {status.anwesend && offen
                        ? 'Anwesend seit ' + offen.kommenZeit + ' Uhr (' + offen.tagFormatiert + ')'
                        : 'Nicht anwesend'}
                </Alert>

                {meldung && <Alert variant={meldung.variant} className="text-center mb-0" dismissible onClose={() => setMeldung(undefined)}>
                    {meldung.text}
                </Alert>}

                <Button variant="success" size="lg" className="py-4" disabled={busy || status.anwesend}
                    onClick={() => buchen('kommen')}>
                    <FontAwesomeIcon icon={faRightToBracket} className={"left-icon"} />
                    &nbsp;Kommen
                </Button>
                <Button variant="danger" size="lg" className="py-4" disabled={busy || !status.anwesend}
                    onClick={() => buchen('gehen')}>
                    <FontAwesomeIcon icon={faRightFromBracket} className={"left-icon"} />
                    &nbsp;Gehen
                </Button>

                <h5 className="mt-3">Heute</h5>
                {heuteZeiten.length === 0
                    ? <div className="text-muted">Heute noch keine Zeiten gebucht.</div>
                    : <Table striped bordered hover size="md">
                        <thead>
                            <tr>
                                <th>Kommen</th>
                                <th>Gehen</th>
                                <th className="text-end">Stunden</th>
                            </tr>
                        </thead>
                        <tbody>
                            {heuteZeiten.map((z) => (
                                <tr key={z.id} className={z.offen ? 'table-warning' : ''}>
                                    <td>{z.kommenZeit}</td>
                                    <td>{z.offen ? 'offen' : z.gehenZeit}</td>
                                    <td className="text-end">{z.offen ? '' : z.stundenFormatiert}</td>
                                </tr>
                            ))}
                            <tr>
                                <th colSpan={2}>Summe</th>
                                <th className="text-end">{formatStunden(summeMinuten(heuteZeiten))}</th>
                            </tr>
                        </tbody>
                    </Table>}

                <Button variant="primary" onClick={zeitenliste}>
                    <FontAwesomeIcon icon={faTableList} className={"left-icon"} />
                    &nbsp;Gebuchte Zeiten
                </Button>
                <Button variant="secondary" onClick={back}>Zurück</Button>
            </Stack>
        </Container>
    );
}
